import { useState, useEffect, useRef } from 'react';
import shelflixLogoWhite from '@/assets/shelflix-logo-white.png';

const ORANGE = '#FF450C';
const ORANGE_DARK = '#E63A08';

// Dark theme colors
const BG_DARK = '#0f0f0f';
const BG_DARKER = '#0a0a0a';
const BG_CARD = '#1a1a1a';
const TEXT_PRIMARY = '#ffffff';
const TEXT_SECONDARY = '#e0e0e0';
const TEXT_TERTIARY = '#a0a0a0';
const BORDER_DARK = '#2a2a2a';
const PRIMARY = '#FF450C';
const TEXT_DARK = '#ffffff';
const TEXT_LIGHT = '#e0e0e0';

const features = [
  {
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke={ORANGE}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <path d="M8 21h8M12 17v4" />
      </svg>
    ),
    title: 'Dashboard em tempo real',
    desc: 'Visualize campanhas, materiais e estatísticas de distribuição em um painel centralizado e intuitivo.',
  },
  {
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke={ORANGE}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M4 17l6-6-6-6M12 19h8" />
      </svg>
    ),
    title: 'Distribuição automatizada',
    desc: 'Envie conteúdo automaticamente para todos os seus pontos de venda com programação flexível.',
  },
  {
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke={ORANGE}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
      </svg>
    ),
    title: 'Sync instantâneo',
    desc: 'Atualizações propagadas em segundos para centenas de telas em locais diferentes.',
  },
  {
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke={ORANGE}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
    title: 'Controle de acesso',
    desc: 'Permissões granulares por usuário, marca ou estabelecimento. Segurança em cada nível.',
  },
  {
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke={ORANGE}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M18 20V10M12 20V4M6 20v-6" />
      </svg>
    ),
    title: 'Relatórios e analytics',
    desc: 'Métricas detalhadas de desempenho para cada campanha, ponto de venda e período.',
  },
  {
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke={ORANGE}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="3" />
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14" />
      </svg>
    ),
    title: 'Suporte 24/7',
    desc: 'Equipe especializada disponível a qualquer hora para garantir o sucesso das suas campanhas.',
  },
];

const steps = [
  {
    num: '1',
    title: 'Cadastre sua campanha',
    desc: 'Crie campanhas, faça upload dos materiais e defina os pontos de venda onde serão exibidos.',
  },
  {
    num: '2',
    title: 'Configure a distribuição',
    desc: 'Defina horários, frequência de exibição e regras de prioridade para cada local.',
  },
  {
    num: '3',
    title: 'Monitore em tempo real',
    desc: 'Acompanhe o desempenho, visualize métricas e faça ajustes sem precisar sair da plataforma.',
  },
];

const modalCopy = {
  terms: {
    title: 'Termos de Uso',
    path: './Shelflix - Termos de Uso.pdf?embedded=true',
  },
  privacy: {
    title: 'Política de Privacidade',
    path: './Shelflix - Politica de Privacidade.pdf?embedded=true',
  },
};

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setInView(true);
      },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function FadeIn({
  children,
  delay = 0,
  className = '',
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p
      style={{
        color: ORANGE,
        fontSize: 12,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '1.5px',
        marginBottom: 10,
      }}
    >
      {children}
    </p>
  );
}

export default function LandingPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  // const [openFaq, setOpenFaq] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [activeModal, setActiveModal] = useState<null | 'terms' | 'privacy'>(null);
  const ctaRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const handleDemo = () => {
    window.open('https://wa.me/11915115289?text=Ol%C3%A1!%20Gostaria%20de%20solicitar%20uma%20demonstra%C3%A7%C3%A3o%20da%20plataforma%20Shelflix.', '_blank');
  };

  return (
    <div
      style={{
        fontFamily: "'Poppins', 'Segoe UI', sans-serif",
        background: BG_DARK,
        color: TEXT_PRIMARY,
        minHeight: '100vh',
      }}
    >
      {/* Google Font */}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Poppins', sans-serif; background: ${BG_DARK}; color: ${TEXT_PRIMARY}; }
        a { text-decoration: none; color: inherit; }
        .feature-card:hover { border-color: ${ORANGE} !important; box-shadow: 0 8px 32px rgba(255,69,12,0.15) !important; }
        .feature-card:hover .feat-icon { background: ${ORANGE} !important; }
        .feature-card:hover .feat-icon svg { stroke: #fff !important; }
        .btn-primary { background: ${ORANGE}; color: #fff; border: none; padding: 14px 32px; border-radius: 10px; font-weight: 600; font-size: 15px; cursor: pointer; transition: background 0.2s, transform 0.15s; font-family: 'Poppins', sans-serif; }
        .btn-primary:hover { background: ${ORANGE_DARK}; transform: scale(1.03); }
        .btn-outline { background: transparent; color: ${ORANGE}; border: 2px solid ${ORANGE}; padding: 13px 32px; border-radius: 10px; font-weight: 600; font-size: 15px; cursor: pointer; transition: background 0.2s, color 0.2s; font-family: 'Poppins', sans-serif; }
        .btn-outline:hover { background: ${ORANGE}; color: #fff; }
        .nav-link { font-size: 14px; font-weight: 500; color: ${TEXT_SECONDARY}; text-decoration: none; transition: color 0.2s; }
        .nav-link:hover { color: ${ORANGE}; }
        .faq-btn { width: 100%; background: none; border: none; text-align: left; padding: 18px 0; font-size: 15px; font-weight: 500; color: ${TEXT_PRIMARY}; cursor: pointer; display: flex; justify-content: space-between; align-items: center; font-family: 'Poppins', sans-serif; }
        input[type=email] { padding: 13px 18px; border: 1.5px solid ${BORDER_DARK}; border-radius: 10px; font-size: 15px; font-family: 'Poppins', sans-serif; outline: none; transition: border 0.2s; width: 260px; background: ${BG_CARD}; color: ${TEXT_PRIMARY}; }
        input[type=email]:focus { border-color: ${ORANGE}; }
        input[type=email]::placeholder { color: ${TEXT_TERTIARY}; }
        @media (max-width: 768px) {
          .hero-grid { grid-template-columns: 1fr !important; }
          .features-grid { grid-template-columns: 1fr 1fr !important; }
          .stats-grid { grid-template-columns: 1fr 1fr !important; }
          .testimonials-grid { grid-template-columns: 1fr !important; }
          .nav-links-desktop { display: none !important; }
          .nav-cta-desktop { display: none !important; }
          input[type=email] { width: 100% !important; }
          .cta-row { flex-direction: column !important; }
        }
        @media (max-width: 480px) {
          .features-grid { grid-template-columns: 1fr !important; }
          .stats-grid { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>

      {/* NAV */}
      <nav
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: scrolled ? `rgba(10, 10, 10, 0.97)` : BG_DARK,
          borderBottom: scrolled ? `1px solid ${BORDER_DARK}` : `1px solid transparent`,
          backdropFilter: 'blur(8px)',
          transition: 'all 0.3s',
        }}
      >
        <div
          style={{
            maxWidth: 1080,
            margin: '0 auto',
            padding: '14px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img src={shelflixLogoWhite} alt="Shelflix Logo" style={{ height: 36 }} />
          </div>
          <ul className="nav-links-desktop" style={{ display: 'flex', gap: 28, listStyle: 'none' }}>
            {[
              ['Funcionalidades', 'features-section'],
              ['Como funciona', 'how-section'],
              ['Contato', 'cta-section'],
            ].map(([label, id]) => (
              <li key={id}>
                <button
                  onClick={() => scrollTo(id)}
                  className="nav-link"
                  style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>
          <button
            className="btn-primary nav-cta-desktop"
            style={{ padding: '10px 22px', fontSize: 14 }}
            onClick={handleDemo}
          >
            Solicitar Demo
          </button>
        </div>
      </nav>

      {/* HERO */}
      <section
        style={{
          background: `linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 100%)`,
          padding: '100px 24px',
          position: 'relative',
        }}
      >
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <FadeIn>
            <div style={{ textAlign: 'center', maxWidth: 700, margin: '0 auto' }}>
              <div
                style={{
                  display: 'inline-block',
                  padding: '8px 20px',
                  background: `rgba(255,69,12,0.1)`,
                  borderRadius: 100,
                  marginBottom: 20,
                }}
              >
                <span
                  style={{
                    color: PRIMARY,
                    fontSize: 13,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                  }}
                >
                  Solução Completa para PDVs
                </span>
              </div>
              <h1 style={{ fontSize: 56, fontWeight: 800, lineHeight: 1.2, marginBottom: 20, color: TEXT_DARK }}>
                Suas Campanhas Mais Perto dos Clientes
              </h1>
              <p style={{ fontSize: 18, color: TEXT_LIGHT, lineHeight: 1.8, marginBottom: 40 }}>
                A plataforma que conecta suas estratégias de marketing aos pontos de venda em tempo real. Sem
                intermediários, sem atrasos.
              </p>
              <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  className="btn-primary"
                  onClick={handleDemo}
                >
                  Conheça a Plataforma
                </button>
                <button
                  style={{
                    background: 'transparent',
                    color: PRIMARY,
                    border: `2px solid ${PRIMARY}`,
                    padding: '14px 40px',
                    borderRadius: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                  }}
                  onMouseEnter={(e) => {
                    (e.target as HTMLButtonElement).style.background = PRIMARY;
                    (e.target as HTMLButtonElement).style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLButtonElement).style.background = 'transparent';
                    (e.target as HTMLButtonElement).style.color = PRIMARY;
                  }}
                  onClick={() => scrollTo('features-section')}
                >
                  Ver Recursos
                </button>
              </div>
            </div>
          </FadeIn>

          {/* Geometric shapes background */}
          <div
            style={{
              position: 'absolute',
              top: 50,
              right: '20%',
              width: 200,
              height: 200,
              background: `rgba(${255},${69},${12},0.05)`,
              borderRadius: '30% 70% 70% 30%',
              animation: 'none',
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: 40,
              left: '15%',
              width: 150,
              height: 150,
              background: `rgba(${255},${69},${12},0.08)`,
              borderRadius: '70% 30% 30% 70%',
            }}
          />
        </div>
      </section>

      {/* FEATURES */}
      <section id="features-section" style={{ maxWidth: 1080, margin: '0 auto', padding: '72px 24px' }}>
        <FadeIn>
          <SectionLabel>Funcionalidades</SectionLabel>
          <h2 style={{ fontSize: 34, fontWeight: 700, marginBottom: 12, color: TEXT_PRIMARY }}>
            Tudo que você precisa em um só lugar
          </h2>
          <p style={{ color: TEXT_SECONDARY, marginBottom: 48, fontSize: 16 }}>
            Gerencie campanhas, distribua conteúdo e monitore resultados com facilidade
          </p>
        </FadeIn>
        <div className="features-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 22 }}>
          {features.map((f, i) => (
            <FadeIn key={f.title} delay={i * 80}>
              <div
                className="feature-card"
                style={{
                  border: `1px solid ${BORDER_DARK}`,
                  borderRadius: 16,
                  padding: 28,
                  background: BG_CARD,
                  transition: 'border 0.2s, box-shadow 0.2s',
                }}
              >
                <div
                  className="feat-icon"
                  style={{
                    width: 48,
                    height: 48,
                    background: '#2d1810',
                    borderRadius: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 18,
                    transition: 'background 0.2s',
                  }}
                >
                  {f.icon}
                </div>
                <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, color: TEXT_PRIMARY }}>{f.title}</h3>
                <p style={{ fontSize: 13, color: TEXT_SECONDARY, lineHeight: 1.65 }}>{f.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-section" style={{ background: BG_CARD, padding: '72px 24px' }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <FadeIn>
            <SectionLabel>Como funciona</SectionLabel>
            <h2 style={{ fontSize: 34, fontWeight: 700, marginBottom: 48, color: TEXT_PRIMARY }}>
              Do cadastro à veiculação em 3 passos
            </h2>
          </FadeIn>
          {steps.map((s, i) => (
            <FadeIn key={s.num} delay={i * 100}>
              <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      background: ORANGE,
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: 18,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {s.num}
                  </div>
                  {i < steps.length - 1 && (
                    <div style={{ width: 2, height: 40, background: '#4a2a1f', margin: '4px 0' }} />
                  )}
                </div>
                <div style={{ paddingBottom: i < steps.length - 1 ? 8 : 0 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 6, color: TEXT_PRIMARY }}>{s.title}</h3>
                  <p style={{ fontSize: 14, color: TEXT_SECONDARY, lineHeight: 1.7 }}>{s.desc}</p>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section
        id="cta-section"
        ref={ctaRef}
        style={{ background: BG_DARKER, padding: '72px 24px', textAlign: 'center' }}
      >
        <FadeIn>
          <div style={{ maxWidth: 560, margin: '0 auto' }}>
            <h2 style={{ fontSize: 34, fontWeight: 700, marginBottom: 14, color: TEXT_PRIMARY }}>
              Pronto para começar?
            </h2>
            <p style={{ color: TEXT_SECONDARY, fontSize: 16, marginBottom: 32 }}>
              Entre em contato e receba uma demo personalizada para o seu negócio. Sem compromisso.
            </p>
            <div className="cta-row" style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className="btn-primary" onClick={handleDemo}>
                Solicitar Demo
              </button>
            </div>
            {submitted && (
              <div
                style={{
                  marginTop: 18,
                  background: '#166534',
                  color: '#86efac',
                  padding: '12px 20px',
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                ✓ Ótimo! Entraremos em contato em breve.
              </div>
            )}
          </div>
        </FadeIn>
      </section>

      {activeModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 200,
            padding: 24,
          }}
          onClick={() => setActiveModal(null)}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 860,
              height: '100%',
              maxHeight: '90vh',
              background: BG_CARD,
              border: `1px solid ${BORDER_DARK}`,
              borderRadius: 16,
              padding: 28,
              overflow: 'auto',
              boxShadow: '0 16px 60px rgba(0,0,0,0.5)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
              <h3 id="modal-title" style={{ fontSize: 24, fontWeight: 700, color: TEXT_PRIMARY }}>
                {modalCopy[activeModal].title}
              </h3>
              <button
                className="btn-outline"
                style={{ padding: '8px 14px', fontSize: 13 }}
                onClick={() => setActiveModal(null)}
              >
                Fechar
              </button>
            </div>
            <div
              style={{
                marginTop: 18,
                whiteSpace: 'pre-line',
                fontSize: 15,
                lineHeight: 1.8,
                color: TEXT_SECONDARY,
                height: '90%'
              }}
            >
              <iframe
                title={modalCopy[activeModal].title}
                src={`${modalCopy[activeModal].path}#toolbar=0&navpanes=0&scrollbar=0`}
                width={'100%'}
                height={'100%'}
              />
            </div>

          </div>
        </div>
      )}

      {/* FOOTER */}
      <footer style={{ background: BG_DARKER, borderTop: `1px solid ${BORDER_DARK}`, padding: '32px 24px' }}>
        <div
          style={{
            maxWidth: 1080,
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 16,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start', maxWidth: 200, gap: 12 }}>
            <img src={shelflixLogoWhite} alt="Shelflix Logo" style={{ height: 44 }} />
          </div>
          <div style={{ fontSize: 13, color: TEXT_TERTIARY, lineHeight: 1.6 }}>
            <p>Shelflix LTDA</p>
            <p>CNPJ: 57107434/0001-02</p>
            <p>Inscrição Estadual: 35264819887</p>
            <p>Endereço: Rua dos Pinheiros, 448 - Sala 2 (Superior), Pinheiros - SP, São Paulo</p>
            <p>CEP: 05422-000</p>
            <p>Email: contato@shelflix.com.br</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <p>Celular: (11) 91511-5289</p>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" className="bi bi-whatsapp" viewBox="0 0 16 16">
                <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
              </svg>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start', gap: 16 }}>
            <button
              onClick={() => setActiveModal('privacy')}
              style={{
                fontSize: 13,
                color: TEXT_TERTIARY,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => (e.target as HTMLButtonElement).style.color = ORANGE}
              onMouseLeave={(e) => (e.target as HTMLButtonElement).style.color = TEXT_TERTIARY}
            >
              Política de Privacidade
            </button>
            <button
              onClick={() => setActiveModal('terms')}
              style={{
                fontSize: 13,
                color: TEXT_TERTIARY,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => (e.target as HTMLButtonElement).style.color = ORANGE}
              onMouseLeave={(e) => (e.target as HTMLButtonElement).style.color = TEXT_TERTIARY}
            >
              Termos de Uso
            </button>
          </div>
        </div>
      </footer>
      <div style={{ background: 'black', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '12px 24px' }}>
        <p style={{ fontSize: 13, color: TEXT_TERTIARY }}>© 2026 Shelflix. Todos os direitos reservados.</p>
      </div>
    </div>
  );
}
